import { observable, action, decorate } from "mobx";

/*
 * The write-back target for copilot compose mode.
 *
 * The wand's whole value is that its output lands in the editor it was opened
 * from. Once the prompt box moves into the drawer -- which outlives the editor
 * -- the drawer has to know which editor a draft belongs to, and whether that
 * editor is still on screen. So editors register a handle here on mount and
 * drop it on unmount; compose mode exists only while a handle does, and drafts
 * for an editor that has gone away become read-only rather than writing into
 * whatever happens to be mounted next.
 *
 * The handle carries the generate/apply closures themselves (not raw fields):
 * generation keeps running through the existing writing-assistant path, so the
 * subject parsing, merge-tag conversion, typewriter and paywall handling are
 * shared rather than reimplemented in the drawer.
 */

// Flip to false to put every surface back on the inline card.
export const COMPOSE_IN_COPILOT = true;

class CopilotEditorStore {
  target = null;
  // The drawer unmounts its subtree when closed, so the conversation lives here
  // instead -- closing the drawer mid-draft and reopening it should not throw
  // away the versions you were comparing. A different editor starts fresh.
  thread = [];
  pendingRewrite = null;
  // The fragment the user asked about, captured at trigger time. docVersion is
  // the guard: offsets into a document that has since changed would splice the
  // rewrite into the wrong place, so a stale selection is refused rather than
  // applied.
  selection = null;
  // Registered by the editor itself (not by RewriteAi) because reading and
  // writing a range needs the Quill instance, and importing quillEditor from
  // RewriteAi would be circular.
  selectionApi = null;
  // A suggestion shown in the editor but not yet accepted. Its markup is in the
  // document, so saving now would send both versions of the passage.
  pendingPreview = false;
  // Count of alternatives behind the one previewed inline, so the editor can
  // offer "see the rest" without listing them in a 450px column.
  moreOptions = 0;

  register(handle) {
    if (!handle || !handle.id) return;
    if (!this.target || this.target.id !== handle.id) this.thread = [];
    this.target = handle;
  }

  pushEntry(entry) {
    this.thread = [...this.thread, entry];
  }

  // An entry point outside the drawer (the readability score's "Rewrite with
  // AI") asking compose mode to run a rewrite as soon as it opens.
  setPendingPreview(value) {
    this.pendingPreview = !!value;
    if (!value) this.moreOptions = 0;
  }

  setMoreOptions(count) {
    this.moreOptions = count || 0;
  }

  setSelectionApi(api) {
    this.selectionApi = api;
  }

  setSelection(selection) {
    this.selection = selection;
  }

  clearSelection() {
    if (this.selection && this.selectionApi) this.selectionApi.clearMark();
    this.selection = null;
  }

  requestRewrite(prompt = "", emotionId = "") {
    this.pendingRewrite = { prompt, emotionId, at: Date.now() };
  }

  consumeRewrite() {
    const pending = this.pendingRewrite;
    this.pendingRewrite = null;
    return pending;
  }

  // Identity-checked: a remount can register before the old instance's cleanup
  // runs, and an unconditional clear would drop the live handle.
  unregister(id) {
    if (this.target && this.target.id === id) this.target = null;
    this.selection = null;
    this.selectionApi = null;
    this.pendingPreview = false;
  }
}

decorate(CopilotEditorStore, {
  target: observable.ref,
  thread: observable.ref,
  pendingRewrite: observable.ref,
  selection: observable.ref,
  selectionApi: observable.ref,
  pendingPreview: observable,
  moreOptions: observable,
  register: action,
  setPendingPreview: action,
  setMoreOptions: action,
  setSelectionApi: action,
  setSelection: action,
  clearSelection: action,
  pushEntry: action,
  requestRewrite: action,
  consumeRewrite: action,
  unregister: action
});

const copilotEditorStore = new CopilotEditorStore();

export { copilotEditorStore };
export default copilotEditorStore;
